import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowUpRight } from 'lucide-react';
import { Button } from '../ui/button';
import { isOnFreePlan, getSubscription } from '../../utils/subscriptionStatus';

const SESSION_KEY = 'upgrade_banner_dismissed';

const PREMIUM_MODULES_TEXT = 'Payroll, Reports, Library, Transport, WhatsApp Integration, Reception, and many more.';

export default function UpgradePromoBanner({
  user,
  compact = false,
  variant,
  className = '',
}) {
  const mode = variant ?? (compact ? 'compact' : 'default');

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch {
      return false;
    }
  });

  const sub = getSubscription(user);
  const eligible = isOnFreePlan(user) || sub.status === 'none';
  const show = mode === 'slim' ? eligible : !dismissed && eligible;
  if (!show || user?.role_slug !== 'school_admin') return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  const planLabel = sub.plan_name || 'Free';

  if (mode === 'slim') {
    return (
      <div className={`bg-amber-500/15 border-b border-amber-500/30 px-4 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${className}`}>
        <p className="text-sm text-foreground">
          You are on the <span className="font-semibold">{planLabel}</span> plan — upgrade now to unlock more features.
        </p>
        <Button size="sm" className="bg-blue-600 shrink-0" asChild>
          <Link to="/settings/subscription/upgrade">Upgrade Now</Link>
        </Button>
      </div>
    );
  }

  if (mode === 'compact') {
    return (
      <div className={`rounded-lg border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-xs ${className}`}>
        <p className="font-medium text-foreground">Upgrade for premium modules</p>
        <Button size="sm" variant="link" className="h-auto p-0 text-blue-600" asChild>
          <Link to="/settings/subscription/upgrade">Upgrade Now</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className={`relative rounded-lg border border-blue-500/30 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 px-4 py-4 ${className}`}>
      <button
        type="button"
        onClick={dismiss}
        className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss banner"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="pr-8">
        <h3 className="font-semibold text-foreground">Upgrade Your Plan for a Better Experience</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Unlock premium modules like {PREMIUM_MODULES_TEXT}
        </p>
        <Button className="mt-3 bg-blue-600" size="sm" asChild>
          <Link to="/settings/subscription/upgrade">
            <ArrowUpRight className="w-4 h-4 mr-1" />
            Upgrade Now
          </Link>
        </Button>
      </div>
    </div>
  );
}
